import React, { useCallback, useEffect, useRef, useState } from "react";
import { AxiosResponse } from "axios";
import { postRequest, patchRequest } from "../../../../../utils/httpRequest";
import { errorNotification } from "../../../../../utils/helpers";
import WIDGETS from "./widgets";
import { InteractivePayload } from "./types";

interface Props {
  question: any;
  enrolment: any;
  answer: any;
  currentUser?: any;
  refreshAnswers: () => void;
  onSubmitted?: (answer: any) => void;
}

const AUTOSAVE_DELAY = 800;

/**
 * Dispatcher for interactive challenges.
 *
 * Owns the whole answer lifecycle so the individual widgets stay dumb: they
 * receive a payload and an `update` callback, and never talk to the API.
 *
 * Interactive challenges are the one question type whose answer exists before
 * the learner submits anything — several screens are pre-populated from earlier
 * answers, so we ask the server to seed one on mount.
 *
 * This file must never branch on a widget key beyond the WIDGETS lookup.
 */
const Interactive: React.FC<Props> = ({
  question,
  enrolment,
  answer,
  currentUser,
  refreshAnswers,
  onSubmitted,
}) => {
  const [payload, setPayload] = useState<InteractivePayload>({});
  const [ready, setReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState<string>("pending");

  const saveTimer = useRef<number | undefined>(undefined);
  const latestPayload = useRef<InteractivePayload>({});
  const container = useRef<HTMLDivElement | null>(null);

  const endpoint = (action: string) =>
    `/api/answers/${action}.json?enrolment_id=${enrolment?.id}&question_id=${question?.id}`;

  // Seed on first view. Idempotent server-side, so a refresh does not reshuffle
  // a picker the learner has already started.
  useEffect(() => {
    if (!question?.id || !enrolment?.id) return;

    let cancelled = false;

    postRequest(endpoint("prepare"), {})
      .then((response: AxiosResponse) => {
        if (cancelled) return;
        const data = response.data || {};
        setPayload(data.payload || {});
        latestPayload.current = data.payload || {};
        setState(data.state || "pending");
        setReady(true);
      })
      .catch((error) => {
        if (cancelled) return;
        (window as any).honeybadger?.notify(error?.response?.data?.error);
        errorNotification(
          "Error loading this exercise",
          JSON.stringify(error?.response?.data),
        );
      });

    return () => {
      cancelled = true;
      window.clearTimeout(saveTimer.current);
    };
  }, [question?.id, enrolment?.id]);

  /**
   * Re-read the answer.
   *
   * Part of a payload can be produced elsewhere — the life map PDF is rendered
   * by a background job after the previous challenge passes — so a widget needs
   * a way to ask again without reloading the page. `prepare` is the read: it
   * finds the existing answer and recomputes the values the widget contributes
   * at render time.
   */
  const refresh = useCallback(
    () =>
      postRequest(endpoint("prepare"), {})
        .then((response: AxiosResponse) => {
          const next = response.data?.payload || {};
          setPayload(next);
          latestPayload.current = next;
        })
        .catch(() => {
          // A failed poll is not worth a notification — the next tick retries.
        }),
    [question?.id, enrolment?.id],
  );

  const persist = useCallback(
    (next: InteractivePayload, submit: boolean) =>
      patchRequest(endpoint("save") + (submit ? "&submit=true" : ""), {
        answer: { payload: next },
      }),
    [question?.id, enrolment?.id],
  );

  // Autosave. Debounced so a pill-picker click storm is one request, and the
  // learner never loses work by navigating away mid-exercise.
  const update = useCallback(
    (next: InteractivePayload) => {
      setPayload(next);
      latestPayload.current = next;

      window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(() => {
        persist(next, false).catch((error) => {
          (window as any).honeybadger?.notify(error?.response?.data?.error);
        });
      }, AUTOSAVE_DELAY);
    },
    [persist],
  );

  const submit = useCallback(() => {
    // Flush any pending autosave first — submitting must not race the debounce.
    window.clearTimeout(saveTimer.current);
    setSubmitting(true);

    return persist(latestPayload.current, true)
      .then((response: AxiosResponse) => {
        setState(response.data?.state || "passed");
        // The submitted payload is not the whole answer — several widgets show
        // read-only values the server computes on pass.
        setPayload(response.data?.payload || latestPayload.current);
        refreshAnswers();

        // An edit view can be several screens tall and its summary two lines,
        // so submitting used to leave the learner scrolled past everything.
        // Bring the exercise back to the top of the viewport as it collapses.
        window.requestAnimationFrame(() =>
          container.current
            ?.closest(".current-lesson-assignment")
            ?.scrollIntoView({ behavior: "smooth", block: "start" }),
        );

        if (onSubmitted) onSubmitted(response.data);
      })
      .catch((error) => {
        (window as any).honeybadger?.notify(error?.response?.data?.error);
        errorNotification(
          "Error submitting your answer",
          JSON.stringify(error?.response?.data),
        );
      })
      .finally(() => setSubmitting(false));
  }, [persist, refreshAnswers, onSubmitted]);

  const Widget = WIDGETS[question?.widget];

  if (!Widget) {
    // A manifest entry without an implementation is a deploy-order problem, not
    // a learner-facing one. Fail quietly rather than crashing the lesson page.
    (window as any).honeybadger?.notify(
      `No interactive widget registered for "${question?.widget}"`,
    );
    return null;
  }

  if (!ready) return <div className="ui active inline loader" />;

  return (
    <div className="interactive-challenge" ref={container}>
      {/* The title is rendered by Assignment as the section heading — see
          question.challenge_title there. Rendering it again here put a second,
          unstyled copy on the purple background. */}
      <Widget
        payload={payload}
        update={update}
        submit={submit}
        submitting={submitting}
        finished={state !== "pending"}
        question={question}
        currentUser={currentUser}
        refresh={refresh}
      />
    </div>
  );
};

export default Interactive;
